import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { PRIORITY_CLIFF } from '@utils/types/back-office/priority-queue';

type PriorityTier = {
  labelKey: string;
  bg: string;
  text: string;
  border: string;
};

function getTier(priority: number): PriorityTier {
  if (priority < 150) {
    return {
      labelKey: 'priority-tier-urgent',
      bg: 'bg-red-500/10',
      text: 'text-red-600',
      border: 'border-red-500/30'
    };
  }
  if (priority < 200) {
    return {
      labelKey: 'priority-tier-expedited',
      bg: 'bg-green-500/10',
      text: 'text-green-700',
      border: 'border-green-500/30'
    };
  }
  if (priority < PRIORITY_CLIFF) {
    return {
      labelKey: 'priority-tier-default',
      bg: 'bg-gray-500/10',
      text: 'text-gray-600',
      border: 'border-gray-400/30'
    };
  }
  if (priority < 500) {
    return {
      labelKey: 'priority-tier-shelved',
      bg: 'bg-zinc-800/20',
      text: 'text-zinc-500',
      border: 'border-zinc-600/40'
    };
  }
  if (priority < 800) {
    return {
      labelKey: 'priority-tier-fair-use',
      bg: 'bg-amber-500/10',
      text: 'text-amber-700',
      border: 'border-amber-500/30'
    };
  }
  return {
    labelKey: 'priority-tier-parked',
    bg: 'bg-rose-900/10',
    text: 'text-rose-800',
    border: 'border-rose-800/30'
  };
}

type Props = { priority: number };

export function PriorityBadge({ priority }: Props) {
  const { t } = useTranslation();
  const tier = getTier(priority);
  const isShelved = priority >= PRIORITY_CLIFF;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-2xl border text-xs font-semibold',
        tier.bg,
        tier.text,
        tier.border
      )}
    >
      <span className="font-mono">{priority}</span>
      <span className="opacity-70">·</span>
      <span>{t(tier.labelKey)}</span>
      {isShelved && (
        <span
          title={t('cliff-shelved-warning')}
          className="ml-0.5 opacity-60 cursor-help"
          aria-label={t('cliff-shelved-warning')}
        >
          ⚑
        </span>
      )}
    </span>
  );
}
